import type { PaginatedResponse } from "@/domain/types/pagination";

import apiClient from "./client";

export const API_LIST_PAGE_SIZE = 100;

export interface PageResult<T> {
  items: T[];
  totalPages?: number;
  pageSize?: number;
}

type ApiListResponse<T> =
  | PaginatedResponse<T>
  | T[]
  | {
      items: T[];
    };

export async function fetchAllPagesFrom<T>(
  fetchPage: (page: number, pageSize?: number) => Promise<PageResult<T>>,
  pageSize?: number,
): Promise<T[]> {
  const firstPage = await fetchPage(1, pageSize);
  const items = [...firstPage.items];
  const totalPages = firstPage.totalPages ?? 1;
  const nextPageSize = firstPage.pageSize ?? pageSize ?? API_LIST_PAGE_SIZE;

  for (let page = 2; page <= totalPages; page += 1) {
    const nextPage = await fetchPage(page, nextPageSize);
    items.push(...nextPage.items);
  }

  return items;
}

export async function fetchAllPages<T>(
  url: string,
  params?: object,
): Promise<T[]> {
  return fetchAllPagesFrom(async (page, pageSize) => {
    const { data } = await apiClient.get<ApiListResponse<T>>(url, {
      params: { ...params, page, pageSize },
    });

    if (Array.isArray(data)) {
      return { items: data };
    }

    return "pagination" in data
      ? {
          items: data.items,
          totalPages: data.pagination.totalPages,
          pageSize: data.pagination.pageSize,
        }
      : { items: data.items };
  }, API_LIST_PAGE_SIZE);
}